import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';

interface LogEntry {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string | null;
  municipio_nome: string | null;
  action: string;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

type LogAction = 'login' | 'logout' | 'all';

const PAGE_SIZE = 20;

export default function LogsTab() {
  const { isAdmin } = useAuth();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionFilter, setActionFilter] = useState<LogAction>('all');
  const [pesquisa, setPesquisa] = useState('');
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [dateRange, setDateRange] = useState<'7' | '30' | '90' | 'all'>('30');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let query = supabase
        .from('session_logs')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false });

      if (actionFilter !== 'all') {
        query = query.eq('action', actionFilter);
      }

      if (dateRange !== 'all') {
        const daysAgo = new Date();
        daysAgo.setDate(daysAgo.getDate() - parseInt(dateRange, 10));
        query = query.gte('created_at', daysAgo.toISOString());
      }

      const { data, count, error: err } = await query
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      if (err) throw err;
      setLogs((data ?? []) as LogEntry[]);
      setTotalCount(count ?? 0);
    } catch {
      setError('Erro ao carregar logs. Verifica as permissões.');
      setLogs([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [actionFilter, dateRange, page]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchLogs();
  }, [fetchLogs, isAdmin]);

  // Reset page quando filtros mudam
  useEffect(() => {
    setPage(0);
  }, [actionFilter, dateRange, pesquisa]);

  const formatDate = (dateStr: string): string => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const actionLabel = (action: string): string => {
    switch (action) {
      case 'login': return 'Início de sessão';
      case 'logout': return 'Fim de sessão';
      default: return action;
    }
  };

  const actionColor = (action: string): string => {
    switch (action) {
      case 'login': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'logout': return 'bg-amber-50 text-amber-700 border-amber-200';
      default: return 'bg-zinc-50 text-zinc-600 border-zinc-200';
    }
  };

  const actionIcon = (action: string): string => {
    switch (action) {
      case 'login': return 'ri-login-box-line';
      case 'logout': return 'ri-logout-box-r-line';
      default: return 'ri-question-line';
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const canGoPrev = page > 0;
  const canGoNext = page < totalPages - 1;

  const logsFiltrados = pesquisa.trim()
    ? logs.filter((l) =>
        l.user_email.toLowerCase().includes(pesquisa.toLowerCase()) ||
        (l.user_name ?? '').toLowerCase().includes(pesquisa.toLowerCase()) ||
        (l.municipio_nome ?? '').toLowerCase().includes(pesquisa.toLowerCase())
      )
    : logs;

  if (!isAdmin) {
    return (
      <div className="bg-white rounded-2xl border border-zinc-100 p-10 text-center">
        <div className="w-12 h-12 flex items-center justify-center bg-zinc-100 rounded-full mx-auto mb-4">
          <i className="ri-shield-cross-line text-zinc-400 text-lg" />
        </div>
        <p className="text-[13px] text-zinc-500">Acesso restrito a administradores.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header + filtros */}
      <div className="bg-white rounded-2xl border border-zinc-100 p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
            <i className="ri-fingerprint-line text-zinc-600 text-sm" />
          </div>
          <div>
            <h2 className="text-[14px] font-semibold text-zinc-800">Logs de Atividade</h2>
            <p className="text-[11px] text-zinc-400 mt-0.5">
              Registo de inícios e fins de sessão dos utilizadores
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
          {/* Pesquisa */}
          <div className="relative flex-1 min-w-[200px]">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <div className="w-4 h-4 flex items-center justify-center text-zinc-400">
                <i className="ri-search-2-line text-sm" />
              </div>
            </div>
            <input
              type="text"
              placeholder="Pesquisar por email, nome ou município..."
              value={pesquisa}
              onChange={(e) => setPesquisa(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-400 transition-colors"
            />
            {pesquisa && (
              <button
                onClick={() => setPesquisa('')}
                className="absolute inset-y-0 right-3 flex items-center text-zinc-400 hover:text-zinc-600 cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-close-line text-sm" />
                </div>
              </button>
            )}
          </div>

          {/* Filtro ação */}
          <div className="flex gap-2">
            {([
              { value: 'all' as LogAction, label: 'Todas' },
              { value: 'login' as LogAction, label: 'Entradas' },
              { value: 'logout' as LogAction, label: 'Saídas' },
            ]).map((f) => (
              <button
                key={f.value}
                onClick={() => setActionFilter(f.value)}
                className={`px-3 py-2 text-[12px] font-semibold rounded-lg border transition-all cursor-pointer whitespace-nowrap ${
                  actionFilter === f.value
                    ? 'bg-zinc-900 text-white border-zinc-900'
                    : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Filtro data */}
          <div className="flex gap-2">
            {([
              { value: '7' as const, label: '7 dias' },
              { value: '30' as const, label: '30 dias' },
              { value: '90' as const, label: '90 dias' },
              { value: 'all' as const, label: 'Tudo' },
            ]).map((f) => (
              <button
                key={f.value}
                onClick={() => setDateRange(f.value)}
                className={`px-3 py-2 text-[12px] font-semibold rounded-lg border transition-all cursor-pointer whitespace-nowrap ${
                  dateRange === f.value
                    ? 'bg-zinc-900 text-white border-zinc-900'
                    : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-14">
            <i className="ri-loader-4-line text-2xl text-zinc-300 animate-spin" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-2 py-14">
            <div className="w-10 h-10 flex items-center justify-center bg-red-50 rounded-full">
              <i className="ri-error-warning-line text-red-400 text-lg" />
            </div>
            <p className="text-[13px] text-red-600">{error}</p>
          </div>
        ) : logsFiltrados.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-14">
            <div className="w-10 h-10 flex items-center justify-center bg-zinc-100 rounded-full">
              <i className="ri-inbox-line text-zinc-400 text-lg" />
            </div>
            <p className="text-[13px] text-zinc-400">
              {pesquisa ? `Nenhum resultado para "${pesquisa}"` : 'Nenhum registo neste período'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-zinc-100">
                  <th className="px-6 py-3 text-[11px] font-semibold text-zinc-400 uppercase tracking-wider">Ação</th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-zinc-400 uppercase tracking-wider">Utilizador</th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-zinc-400 uppercase tracking-wider">Município</th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-zinc-400 uppercase tracking-wider">Data</th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-zinc-400 uppercase tracking-wider hidden md:table-cell">IP</th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-zinc-400 uppercase tracking-wider hidden lg:table-cell">User Agent</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-50">
                {logsFiltrados.map((log) => (
                  <tr key={log.id} className="hover:bg-zinc-50/60 transition-colors">
                    <td className="px-6 py-3.5">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-full border ${actionColor(log.action)}`}>
                        <i className={actionIcon(log.action)} />
                        {actionLabel(log.action)}
                      </span>
                    </td>
                    <td className="px-6 py-3.5">
                      <div>
                        <p className="text-[13px] font-medium text-zinc-800">{log.user_name || '—'}</p>
                        <p className="text-[11px] text-zinc-400">{log.user_email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-3.5">
                      <span className="text-[13px] text-zinc-600">{log.municipio_nome || '—'}</span>
                    </td>
                    <td className="px-6 py-3.5">
                      <span className="text-[13px] text-zinc-600 whitespace-nowrap">{formatDate(log.created_at)}</span>
                    </td>
                    <td className="px-6 py-3.5 hidden md:table-cell">
                      <span className="text-[12px] text-zinc-400 font-mono">{log.ip_address || '—'}</span>
                    </td>
                    <td className="px-6 py-3.5 hidden lg:table-cell max-w-[200px] truncate">
                      <span className="text-[11px] text-zinc-400" title={log.user_agent ?? undefined}>
                        {log.user_agent ? log.user_agent.slice(0, 60) + (log.user_agent.length > 60 ? '...' : '') : '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginação */}
        {!loading && logsFiltrados.length > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-zinc-100">
            <p className="text-[12px] text-zinc-400">
              Página {page + 1} de {totalPages} · {totalCount} registos no total
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={!canGoPrev}
                className={`flex items-center gap-1 px-3 py-1.5 text-[12px] font-semibold rounded-lg border transition-all cursor-pointer ${
                  canGoPrev
                    ? 'bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400'
                    : 'bg-zinc-50 text-zinc-300 border-zinc-100 cursor-not-allowed'
                }`}
              >
                <i className="ri-arrow-left-s-line" /> Anterior
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={!canGoNext}
                className={`flex items-center gap-1 px-3 py-1.5 text-[12px] font-semibold rounded-lg border transition-all cursor-pointer ${
                  canGoNext
                    ? 'bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400'
                    : 'bg-zinc-50 text-zinc-300 border-zinc-100 cursor-not-allowed'
                }`}
              >
                Próxima <i className="ri-arrow-right-s-line" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}